import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import { useT } from '@/i18n';
import { downloadText } from '@/lib/download';
import { Markdown } from '@/lib/markdown';
import { formatMoney } from '@/lib/money';
import { formatMonthDay, formatShortDate } from '@/lib/time';
import { formatNYClock, todayNY } from '@/market/marketHours';
import { actualCost, BUILTIN_PRICES_AS_OF, formatUsd, priceFor } from '@/reports/cost';
import { usePriceFeed } from '@/reports/priceFeed';
import { generateReport } from '@/reports/generate';
import { templateById, templateName } from '@/reports/templates';
import { hasLLM, useSettings } from '@/settings/settingsStore';
import { useAppState } from '@/state/appStore';
import s from './reports.module.css';

export function ReportView() {
  const { t } = useT();
  const { reportId } = useParams();
  const app = useAppState();
  const settings = useSettings();
  const navigate = useNavigate();
  const report = reportId ? app.reports[reportId] : undefined;
  if (!report) return <Navigate to="/reports" replace />;

  const template = templateById(report.templateId);
  const name = templateName(report.templateId);
  const isToday = report.date === todayNY();
  const busy = app.reportBusy === report.templateId;
  const usage = report.usage;
  const feed = usePriceFeed();
  const price = priceFor(report.provider, report.model, feed);
  // A report generated before the price feed knew its model is priced once the feed does.
  const costUsd = report.costUsd ?? actualCost(report.provider, report.model, usage, feed);
  const searches = usage?.searches ?? report.queries?.length ?? 0;
  const searchFee = price ? searches * price.searchFee : null;
  const tokenCost = price && costUsd != null ? costUsd - (searchFee ?? 0) : null;
  const costNote = !price
    ? t('report.costUnknown')
    : price.source === 'builtin'
      ? t('report.costNoteBuiltin', { date: BUILTIN_PRICES_AS_OF })
      : price.approx
        ? t('report.costNoteApprox', { model: report.model, date: formatShortDate(feed?.fetchedAt ?? Date.now()) })
        : t('report.costNoteFeed', { date: formatShortDate(feed?.fetchedAt ?? Date.now()) });

  const regenerate = () => {
    generateReport(report.templateId, { scenario: report.inputs.scenario }).then((r) => navigate(`/reports/${r.id}`, { replace: true })).catch(() => undefined);
  };

  return (
    <div className="page">
      <Header crumbs={[{ label: t('header.list'), to: '/' }, { label: t('reports.title'), to: '/reports' }]} title={name} showCurrency={false} actions={
        <div style={{ display: 'flex', gap: 10 }}>
          {isToday && hasLLM(settings) && <button type="button" className="btn quiet" disabled={busy} onClick={regenerate}>{busy ? t('reports.running') : t('report.regenerate')}</button>}
          <button type="button" className="btn quiet" onClick={() => downloadText(`assetlist-${report.templateId}-${report.date}.md`, report.markdown)}>{t('report.export')}</button>
          <button type="button" className="btn primary" onClick={() => navigate('/experts')}>{t('report.askExperts')}</button>
        </div>
      } />

      <section className={s.titleRow}>
        <h2 className={`serif ${s.h2Report}`}>{formatMonthDay(report.date)} · {name}</h2>
        <span className="muted num" style={{ fontSize: 13 }}>
          {t('report.meta', { time: formatNYClock(report.generatedAt), model: report.model })}
          {template.needsWeb && searches > 0 ? ` · ${t('report.metaSearches', { n: searches })}` : ''}
          {costUsd != null ? ` · ${formatUsd(costUsd)}` : ''}
        </span>
        {isToday && template.daily && <span className="badge" style={{ marginLeft: 'auto' }}>{t('report.today')}</span>}
      </section>

      <section className={s.reportBody}>
        <aside>
          <div className="label" style={{ padding: '14px 0 6px' }}>{t('report.inputs')}</div>
          <div className={s.snap}>
            <span style={{ fontWeight: 500 }}>{t('report.snapshot')}</span>
            <div className={s.kv}>
              <span className="muted">{t('report.total')}</span><span className={`num ${s.kvV}`}>{formatMoney(report.inputs.totalUsd, 'USD')}</span>
              <span className="muted">{t('report.positions')}</span><span className={`num ${s.kvV}`}>{report.inputs.positions}</span>
              <span className="muted">{t('report.fxDate')}</span><span className={`num ${s.kvV}`}>{report.inputs.fxDate ?? '—'}</span>
              {report.inputs.scenario && (<><span className="muted">{t('report.scenario')}</span><span className={s.kvV}>{report.inputs.scenario}</span></>)}
            </div>
          </div>
          {report.queries && report.queries.length > 0 && (
            <div className={s.snap}>
              <span style={{ fontWeight: 500 }}>{t('report.queries', { n: report.queries.length })}</span>
              <div className={s.chips}>{report.queries.map((q, i) => <span key={i} className={s.chip}>{q}</span>)}</div>
            </div>
          )}
          {usage && (usage.inputTokens != null || usage.outputTokens != null || usage.searches != null) && (
            <div className={s.snap}>
              <span style={{ fontWeight: 500 }}>{t('report.cost')}</span>
              <div className={s.kv}>
                {template.needsWeb && (<><span className="muted">{t('report.costSearches', { n: searches })}</span><span className={`num ${s.kvV}`}>{searchFee == null ? '—' : formatUsd(searchFee)}</span></>)}
                <span className="muted">{t('report.costTokens', { i: (usage.inputTokens ?? 0).toLocaleString('en-US'), o: (usage.outputTokens ?? 0).toLocaleString('en-US') })}</span>
                <span className={`num ${s.kvV}`}>{tokenCost == null ? '—' : formatUsd(tokenCost)}</span>
                <span style={{ fontWeight: 500 }}>{t('report.costTotal')}</span><span className={`num ${s.kvV}`} style={{ fontWeight: 500 }}>{formatUsd(costUsd)}</span>
              </div>
              <span className="muted" style={{ fontSize: 12 }}>{costNote}</span>
            </div>
          )}
        </aside>

        <article className={s.article}>
          <Markdown text={report.markdown} />
          {report.searchSuggestionsHtml && <div className={s.suggest} dangerouslySetInnerHTML={{ __html: report.searchSuggestionsHtml }} />}
          <p className="muted" style={{ fontSize: 12, marginTop: 24, borderTop: '1px solid var(--rule-soft)', paddingTop: 12 }}>{t('report.disclaimer')}</p>
        </article>
      </section>
    </div>
  );
}
